/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { createSlice, PayloadAction, type ActionReducerMapBuilder } from "@reduxjs/toolkit"
import { submissionCommitted } from "features/userParameters/submissionCommitted"
import { CurrentUserParameters } from "features/userParameters/UserParametersSchemata"
import { DeepReadonly } from "utils/readonly"

// AIDEV-NOTE: Keep reducer inputs deeply readonly and return new state objects for strict readonly linting.

interface UserParametersState {
  parameters: CurrentUserParameters | null
}

const initialState: UserParametersState = {
  parameters: null,
}

// Shared by setLastSubmittedParameters and submissionCommitted, so the two record a
// submission identically.
const recordSubmittedParameters = (
  state: DeepReadonly<UserParametersState>,
  parameters: DeepReadonly<CurrentUserParameters>
): UserParametersState => ({
  ...(state as UserParametersState),
  parameters: parameters as unknown as CurrentUserParameters,
})

const lastSubmittedParametersSlice = createSlice({
  name: "lastSubmittedParameters",
  initialState: initialState,
  reducers: {
    setLastSubmittedParameters: (
      state: DeepReadonly<UserParametersState>,
      action: DeepReadonly<PayloadAction<DeepReadonly<CurrentUserParameters>>>
    ): UserParametersState => recordSubmittedParameters(state, action.payload),
  },
  extraReducers: (builder: Readonly<ActionReducerMapBuilder<UserParametersState>>) => {
    builder.addCase(
      submissionCommitted,
      (
        state: DeepReadonly<UserParametersState>,
        action: DeepReadonly<ReturnType<typeof submissionCommitted>>
      ): UserParametersState => recordSubmittedParameters(state, action.payload.parameters)
    )
  },
})

export const { setLastSubmittedParameters } = lastSubmittedParametersSlice.actions
export default lastSubmittedParametersSlice.reducer
